import i18n from '@/i18n'
import store from '@/store'
import { propertyContact } from '@/use/usePropertyContact'
import type { ServerError } from './captureCheckinError'

// Marks the alert as an error so a photo of the screen is diagnostic. Until ENG-2910 a failure on
// the contact page rendered the same title and body as the help button, which is why five reports
// of this bug were read as guests tapping the `?` icon.
export const ERROR_CLASSES = 'border-l-8 border-Error'

/**
 * Builds the alert for a failed API call: the server's own reason where we have no better copy,
 * curated copy where we do, and never an empty modal.
 */
export default function serverErrorAlert(
  error: ServerError | null | undefined
) {
  const { t } = i18n.global
  const curated = curatedErrorCopy(error?.type)

  const title = curated.title ?? t('alerts.errors.somethingWentWrong')
  const messages = curated.message ? [curated.message] : serverMessages(error)

  return store.dispatch('setAlert', {
    title,
    messages: [...messages, helpLine()],
    buttonText: t('general.close'),
    classes: ERROR_CLASSES,
  })
}

/**
 * The curated title and first message for an error type, where the locale files have them. Shared
 * with errorDispatcher, so both resolve copy the same way.
 */
export function curatedErrorCopy(type?: string | null): {
  title?: string
  message?: string
} {
  const { t, te } = i18n.global
  const titleKey = type && `alerts.errors.titles.${type}`
  // Probe the indexed leaf, not the array: `te` reports false for an array path, which would send
  // every curated message down the fallback instead.
  //
  // Only `[0]` is read, so curated copy for a type handled by serverErrorAlert must be a single,
  // parameter-free string. Anything that needs more than one line or an interpolated value --
  // the Booking* keys, for instance -- belongs in errorDispatcher, which owns those types and
  // passes the params. Collecting the array here instead would render literal `{phoneNumber}`.
  const messageKey = type && `alerts.errors.messages.${type}[0]`

  return {
    title: titleKey && te(titleKey) ? t(titleKey) : undefined,
    message: messageKey && te(messageKey) ? t(messageKey) : undefined,
  }
}

// The server already sends a usable reason -- `{type, message, metadata}` from render_error, or
// `{messages: [...]}` from render_record_error. Showing it is what makes the next report of this
// shape diagnosable without a log dive.
function serverMessages(error: ServerError | null | undefined): string[] {
  const { t } = i18n.global
  const fromServer = (error?.messages ?? [error?.message]).filter(
    Boolean
  ) as string[]

  return fromServer.length > 0 ? fromServer : [t('alerts.errors.unspecified')]
}

// A phone number is useless to a guest standing at the front desk holding the property's own iPad.
function helpLine(): string {
  const { t } = i18n.global

  return store.getters.isPropertyDevice
    ? t('alerts.info.frontDesk')
    : t('alerts.info.contactUs', { phoneNumber: propertyContact.value })
}
